import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Field,
  linkTextClasses,
  Page,
  PageHeader,
  Select,
  Table,
} from '@evogard/ui';
import {
  RECORD_ENTITIES,
  type RecordEntity,
  type ClassificationState,
  type SweepResult,
  type ErpHealth,
  type EntitySync,
  type CoreProjectionState,
  type ErpFailedJob,
  type ErpWorker,
} from '@evogard/contract';
import { formatAge, PROGRESS_STALE_MS, type Tone } from '@evogard/domain';
import { api, ok } from '../../lib/api.js';
import { useRealtimeConnected } from '../../lib/realtime-bus.js';
import { can, useSessionContext } from '../../lib/session.js';
import {
  Age,
  CORE_ENTITY_LABEL,
  ENTITY_LABEL,
  BreakerBadge,
  breakerExplanation,
  RefreshOutcome,
  useClassificationState,
  useErpHealth,
  useErpRefresh,
} from './common.js';

/**
 * A saúde do espelho do ERP, na base atual.
 *
 * ## O trabalho inteiro desta tela é não afirmar o que ela não sabe
 *
 * Não é um detalhe de acabamento: é o assunto. A tela mostra o estado de uma
 * integração que responde uma chamada a cada quatro segundos e cujo espelho
 * pode estar horas atrás do mundo. Toda linha aqui existe para separar coisas
 * que os sistemas antigos desenhavam iguais:
 *
 * - **"nunca sincronizou" não é "sincronizou e veio vazio".** As duas viram
 *   `0` na tela ingênua, e a mais importante das duas some. Aqui a primeira é
 *   um selo neutro escrito "nunca sincronizou" e a segunda é "concluída" com
 *   `0` registros.
 * - **"não sei quantos" não é zero.** `records` nulo é falha antes da
 *   primeira página; a coluna diz "não contado".
 * - **"sem falhas na janela" não é "nunca falhou".** Ver `BreakerBadge`.
 * - **"não medimos" não é "zero chamadas hoje"** — a segunda seria uma
 *   afirmação sobre a operação feita a partir da nossa falta de instrumento.
 * - **Enquanto carrega é esqueleto**, nunca um zero e nunca um giro no meio da
 *   página.
 *
 * ## E o estado que domina a tela hoje é a classificação vazia
 *
 * `app.erp_status_classifications` nasce vazia de propósito — o registro não
 * diz qual praça dá qual significado aos códigos que decidem se um veículo
 * aceita cancelamento. Uma tabela vazia com "nenhum item" seria a mentira de
 * interface que a casa proíbe em primeiro lugar; então a tela lê o estado e
 * **diz** o que ele significa na prática e o que o destrava. Ver
 * `ClassificationNotice`.
 *
 * ## O que esta tela não tem, e por quê
 *
 * Nenhum seletor de praça. A resposta é da base da sessão, e a permissão é por
 * grupo — quem tem `erp.mirror:ver` na CE não necessariamente tem na GO. A
 * dimensão "por tenant" do plano é a troca de base do menu.
 */
export function ErpHealthScreen() {
  const { data: context } = useSessionContext();
  /*
   * A releitura é o único jeito de a tela ver o worker cair e a barra andar —
   * nenhum dos dois manda aviso. Com passagem aberta ou sem o tempo real, a
   * cada 15 s; parada, a cada minuto.
   */
  const live = useRealtimeConnected();
  const health = useErpHealth((data) =>
    !live || data?.syncs.some((s) => s.estado === 'em_andamento') ? 15_000 : 60_000,
  );
  const classification = useClassificationState();
  const canSync = can(context, 'erp.sync', 'executar');

  return (
    <Page measure="cheia">
      <PageHeader title="Saúde da integração">
        O espelho do ERP na base{' '}
        <strong className="font-medium text-texto">{context?.currentTenant.nome}</strong>.
        Os números são desta praça — trocar de base troca a resposta inteira.
      </PageHeader>

      {health.isError && (
        <Alert tone="erro" title="Não foi possível ler o estado da integração.">
          {health.error.message}
        </Alert>
      )}

      {/* A leitura da classificação falhou: a tela diz que não sabe, em vez de
          simplesmente não mostrar o aviso — a ausência dele seria lida como
          "está tudo classificado", que é uma afirmação. */}
      {classification.isError && (
        <Alert tone="informacao">
          Não foi possível ler a classificação de situações agora. Esta tela não sabe
          dizer se há códigos por classificar.
        </Alert>
      )}

      <ClassificationNotice estado={classification.data} />

      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <WorkerCard worker={health.data?.worker} />
        <BreakerCard health={health.data} />
        <ConsumptionCard health={health.data} />
      </div>

      {/* Erro sem dado nenhum: o aviso lá em cima é a resposta inteira. A
          tabela abaixo dele anunciaria "nunca sincronizou" para as sete
          entidades — uma afirmação sobre a operação feita quando não sabemos
          nada sobre ela. */}
      {(!health.isError || health.data) && (
        <Card
          title="Sincronização por entidade"
          descricao="A última passagem de cada entidade nesta base."
          bleed
        >
          {/* Sem paginação: são as entidades do espelho, todas de uma vez. Com
              as duas do histórico (14/10/2026) passaram de dez, e a página
              padrão escondia "Atendimentos" e "Alterações" na segunda folha. */}
          <Table
            paginate={false}
            label="Sincronização por entidade"
            items={health.data?.syncs ?? []}
            loading={health.isPending}
            rowKey={(s) => s.entity}
            empty={
              'O servidor não devolveu nenhuma entidade. ' +
              'A resposta traz sempre todas as entidades do espelho — se esta lista está vazia, o problema é a resposta, não o espelho.'
            }
            columns={[
              {
                title: 'Entidade',
                minWidth: '11rem',
                cell: (s) => ENTITY_LABEL[s.entity],
              },
              { title: 'Estado', cell: (s) => <EntityState sync={s} /> },
              {
                title: 'Última passagem',
                minWidth: '13rem',
                cell: (s) => <LastPass sync={s} />,
              },
              {
                title: 'Registros',
                alignment: 'fim',
                cell: (s) => <Records sync={s} />,
              },
              /*
               * A varredura da coleção, por linha.
               *
               * Ela não existia: a passagem em massa só era enfileirada pelo
               * cron das 03:40, e nenhuma rota a chamava. Reconstruir o
               * espelho era esperar o relógio — medido em 21/09/2026, quando a
               * importação de venda ficou nove dias presa atrás de um espelho
               * vencido sem que ninguém pudesse fazer nada.
               *
               * A coluna some inteira sem `erp.sync:executar`, e esconder não
               * protege nada: quem protege é o guard. As duas coisas, sempre.
               */
              ...(canSync
                ? [
                    {
                      title: 'Varredura',
                      alignment: 'fim' as const,
                      minWidth: '12rem',
                      cell: (s: EntitySync) => <SweepButton entity={s.entity} />,
                    },
                  ]
                : []),
            ]}
          />
        </Card>
      )}

      {health.data && (
        <>
          <ProjectionCard projections={health.data.projections} />
          <div className="grid gap-5 sm:grid-cols-2">
            <QueueCard queue={health.data.queue} />
            <FailuresCard failures={health.data.failures} />
          </div>
        </>
      )}

      {canSync && <UpdateRegistry />}
    </Page>
  );
}

/* ------------------------------------------------------------------------- *
 * A classificação vazia — o estado que domina esta tela hoje
 * ------------------------------------------------------------------------- */

/**
 * Diz o que a classificação vazia significa, sem culpar quem está olhando.
 *
 * Enquanto a consulta não respondeu, **não desenha nada**: "nenhuma situação
 * classificada" é uma afirmação, e afirmá-la antes de ler seria o mesmo defeito
 * que esta tela inteira existe para não cometer.
 *
 * O segundo parágrafo muda com o espelho: se as duas tabelas de situação nunca
 * sincronizaram, nem a lista do que **falta** classificar é confiável — o
 * servidor a monta a partir dos códigos que o espelho viu, e ele não viu
 * nenhum. Sem essa distinção, "0 códigos pendentes" pareceria trabalho
 * terminado.
 */
function ClassificationNotice({ estado }: { estado: ClassificationState | undefined }) {
  if (!estado) return null;

  const mirrorNever = estado.mirror.every((e) => e.estado === 'nunca');

  if (estado.classifications.length === 0) {
    return (
      <Alert tone="alerta" title="Nenhuma situação está classificada nesta base.">
        <p className="mt-1">
          A classificação nasce vazia de propósito: o registro do sistema antigo não diz
          qual praça dá qual significado aos códigos que decidem se um veículo aceita
          cancelamento, e preenchê-los por semelhança seria inventar a metade que decide.
        </p>
        <p className="mt-2">
          Enquanto estiver assim, quem pergunta a situação de um boleto ou de um veículo
          recebe <strong className="font-medium">não classificado</strong> — nenhum módulo
          decide cancelamento por código nenhum, e nenhuma esteira supõe no lugar.
        </p>
        <p className="mt-2">
          {mirrorNever
            ? 'Destrava em dois passos: medir o ERP nas duas praças (make sga-medir), o que também preenche o espelho de situações — hoje ele nunca sincronizou, então nem a lista do que falta classificar existe ainda — e depois classificar cada código à mão.'
            : 'Destrava classificando à mão os códigos que o espelho já trouxe.'}
        </p>
        <p className="mt-2">
          <Link to="/erp/classificacoes" className={linkTextClasses()}>
            Abrir a classificação de situações
          </Link>
        </p>
      </Alert>
    );
  }

  if (estado.notClassified.length > 0) {
    return (
      <Alert tone="informacao">
        {estado.notClassified.length === 1
          ? '1 código de situação apareceu no espelho e ainda não foi classificado.'
          : `${estado.notClassified.length} códigos de situação apareceram no espelho e ainda não foram classificados.`}{' '}
        Até lá, quem pergunta por eles recebe “não classificado”.
        <span className="ml-1">
          <Link to="/erp/classificacoes" className={linkTextClasses()}>
            Classificar agora
          </Link>
        </span>
      </Alert>
    );
  }

  return null;
}

/* ------------------------------------------------------------------------- *
 * O worker, o disjuntor e o consumo
 * ------------------------------------------------------------------------- */

// @valor
const WORKER_STATES: Record<ErpWorker['estado'], { label: string; tone: Tone }> = {
  nao_medido: { label: 'Não medido', tone: 'neutro' },
  de_pe: { label: 'De pé', tone: 'sucesso' },
  subindo: { label: 'Subindo', tone: 'informacao' },
  parado: { label: 'Parado', tone: 'erro' },
  travado_na_partida: { label: 'Travado na partida', tone: 'erro' },
  fila_parada: { label: 'Fila parada', tone: 'erro' },
};

/**
 * O processo que roda as esteiras — o card que faltou em 28/10/2026, quando o
 * worker travou na partida e esta tela continuou mostrando a última passagem
 * de cada esteira como se nada tivesse acontecido.
 *
 * O estado chega decidido do servidor (`workerState`, em `@evogard/domain`);
 * aqui ele é dito por extenso, com a ação quando há uma. É um worker só para
 * as duas praças, e o card diz isso: parado na CE é parado na GO.
 */
function WorkerCard({ worker }: { worker: ErpWorker | undefined }) {
  return (
    <Card title="Worker das esteiras">
      {!worker ? (
        <div className="flex flex-col gap-2">
          <span className="esqueleto block h-6 w-32" />
          <span className="esqueleto block h-4 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <Badge tone={WORKER_STATES[worker.estado].tone}>
            {WORKER_STATES[worker.estado].label}
          </Badge>
          <p className="text-sm text-texto-suave">
            <WorkerExplanation worker={worker} />
          </p>
          <p className="text-xs text-texto-suave">Um só para as duas bases.</p>
        </div>
      )}
    </Card>
  );
}

function WorkerExplanation({ worker }: { worker: ErpWorker }) {
  switch (worker.estado) {
    case 'nao_medido':
      /* não é "parado": nada gravado pode ser ambiente sem worker ou versão que não grava */
      return (
        <>
          Nenhum pulso do worker foi gravado. Ou ele não está rodando neste ambiente, ou é
          de uma versão que ainda não grava — esta tela não sabe dizer qual.
        </>
      );
    case 'de_pe':
      return (
        <>
          Último pulso <Age iso={worker.beatAt} />.{' '}
          {worker.lastJobAt ? (
            <>
              A fila do SGA andou pela última vez <Age iso={worker.lastJobAt} />.
            </>
          ) : (
            'Nenhum job da fila do SGA desde a partida.'
          )}
        </>
      );
    case 'subindo':
      return (
        <>
          A partida começou <Age iso={worker.since} />
          {worker.step ? <> e está em: {worker.step}</> : null}.
        </>
      );
    case 'parado':
      return (
        <>
          Sem pulso desde <Age iso={worker.since} />. Nenhuma esteira roda enquanto ele
          estiver parado: reinicie o contêiner do worker.
        </>
      );
    case 'travado_na_partida':
      return (
        <>
          A partida começou <Age iso={worker.since} /> e não terminou
          {worker.step ? <>; parou em: {worker.step}</> : null}. Depois de 3 minutos ele
          sai com erro para ser reiniciado — se continuar assim, reinicie o contêiner do
          worker.
        </>
      );
    case 'fila_parada':
      return (
        <>
          O worker está de pé, mas a fila do SGA não anda: há job esperando desde{' '}
          <Age iso={worker.since} /> e nada rodando
          {worker.lastJobAt ? (
            <>
              {' '}
              — o último andou <Age iso={worker.lastJobAt} />
            </>
          ) : null}
          . Reinicie o contêiner do worker.
        </>
      );
  }
}

function BreakerCard({ health }: { health: ErpHealth | undefined }) {
  return (
    <Card title="Disjuntor desta praça">
      {!health ? (
        <div className="flex flex-col gap-2">
          <span className="esqueleto block h-6 w-48" />
          <span className="esqueleto block h-4 w-full" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <BreakerBadge estado={health.breaker.estado} />
          <p className="text-sm text-texto-suave">{breakerExplanation(health.breaker)}</p>
          <dl className="flex flex-col gap-1 text-xs text-texto-suave">
            {health.breaker.openUntil && (
              <div className="flex gap-1">
                <dt>Espera até:</dt>
                <dd className="text-texto">
                  <Age iso={health.breaker.openUntil} />
                </dd>
              </div>
            )}
            <div className="flex gap-1">
              <dt>Última falha:</dt>
              <dd className="text-texto">
                {health.breaker.lastFailureAt ? (
                  <Age iso={health.breaker.lastFailureAt} />
                ) : (
                  // "nenhuma DENTRO DA JANELA": a tabela é podada, e um
                  // "nenhuma falha" seco viraria uma afirmação sobre a história
                  // inteira da integração
                  <span className="text-texto-suave">nenhuma dentro da janela</span>
                )}
              </dd>
            </div>
          </dl>
        </div>
      )}
    </Card>
  );
}

function ConsumptionCard({ health }: { health: ErpHealth | undefined }) {
  return (
    <Card title="Consumo do dia">
      {!health ? (
        <div className="flex flex-col gap-2">
          <span className="esqueleto block h-7 w-24" />
          <span className="esqueleto block h-4 w-full" />
        </div>
      ) : health.dayconsumo.estado === 'nao_medido' ? (
        /*
         * "Nada contado", e não "0 chamadas".
         *
         * O contador do dia nasce na primeira chamada desta base ao ERP
         * (`hinova/usage.ts`); antes dela não há linha. Um zero aqui diria "a
         * esteira não rodou hoje", que é uma afirmação sobre a operação tirada
         * da ausência de uma linha.
         */
        <div className="flex flex-col gap-1">
          <Badge tone="neutro">Nada contado hoje</Badge>
          <p className="text-sm text-texto-suave">
            Nenhuma chamada desta base ao ERP foi contada hoje. A contagem começa na
            primeira chamada do dia — esta tela não escreve zero no lugar, porque zero
            diria que a esteira não rodou.
          </p>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          <p className="text-xl font-bold tabular-nums">
            {health.dayconsumo.calls}
            <span className="text-sm font-normal text-texto-suave">
              {' '}
              de {health.dayconsumo.dayCap} chamadas
            </span>
          </p>
          <p className="text-sm text-texto-suave">
            {health.dayconsumo.failures === 1
              ? '1 falhou. '
              : `${health.dayconsumo.failures} falharam. `}
            Contado desde <Age iso={health.dayconsumo.since} />.
          </p>
        </div>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * A projeção, a fila e as falhas
 * ------------------------------------------------------------------------- */

/**
 * A projeção espelho → núcleo — a pendência "a PROJEÇÃO não tem tela".
 *
 * É a linha que explica "a esteira fechou e a ficha continua velha": toda tela
 * do cadastro lê o núcleo (regra 16), e o núcleo só muda quando a projeção
 * roda. A esteira lá em cima mede o espelho; esta mede o que as telas leem.
 */
function ProjectionCard({ projections }: { projections: CoreProjectionState[] }) {
  return (
    <Card
      title="Projeção para o cadastro"
      descricao="Do espelho para as tabelas que as telas leem. Uma esteira concluída só aparece nas fichas depois daqui."
      bleed
    >
      <Table
        label="Projeção por entidade"
        items={projections}
        rowKey={(p) => p.entity}
        empty="O servidor não devolveu nenhuma entidade — o problema é a resposta, não a projeção."
        columns={[
          {
            title: 'Entidade',
            minWidth: '11rem',
            cell: (p) => CORE_ENTITY_LABEL[p.entity],
          },
          { title: 'Estado', cell: (p) => <ProjectionState projection={p} /> },
          {
            title: 'Última passagem',
            minWidth: '13rem',
            cell: (p) =>
              p.estado === 'nunca' ? (
                <span className="text-texto-suave">Nunca, nesta base</span>
              ) : p.estado === 'em_andamento' ? (
                <span className="text-sm">
                  começou <Age iso={p.startedAt} />
                </span>
              ) : (
                <Age iso={p.completedAt} />
              ),
          },
          {
            title: 'Gravadas',
            alignment: 'fim',
            cell: (p) =>
              p.estado === 'concluida' && p.inserted !== null && p.updated !== null ? (
                <span className="tabular-nums">
                  {p.inserted + p.updated}
                  {p.missing ? (
                    <span className="text-texto-suave"> · {p.missing} sumiram</span>
                  ) : null}
                </span>
              ) : (
                <span className="text-texto-suave">—</span>
              ),
          },
        ]}
      />
    </Card>
  );
}

function ProjectionState({ projection: p }: { projection: CoreProjectionState }) {
  if (p.estado === 'nunca') return <Badge tone="neutro">Nunca projetada</Badge>;
  if (p.estado === 'em_andamento') return <Badge tone="informacao">Em andamento</Badge>;
  if (p.result === 'sucesso') return <Badge tone="sucesso">Concluída</Badge>;
  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone="erro">Falhou</Badge>
      {p.lastSuccessAt ? (
        <span className="text-xs text-texto-suave">
          as fichas mostram a passagem de <Age iso={p.lastSuccessAt} />
        </span>
      ) : null}
      {p.detail && <span className="text-xs text-texto-suave">{p.detail}</span>}
    </div>
  );
}

/**
 * A fila do ERP desta base. O teto é um só para as duas praças — uma chamada a
 * cada quatro segundos —, e o que a outra base tem na fila não aparece aqui;
 * quando é ela que está com o teto, a tela diz só isso.
 */
function QueueCard({ queue }: { queue: ErpHealth['queue'] }) {
  return (
    <Card title="Fila do ERP">
      <dl className="grid grid-cols-3 gap-3 text-sm">
        <div>
          <dt className="text-texto-suave">Pedidos de tela</dt>
          <dd className="text-xl font-bold tabular-nums">{queue.waiting.screen}</dd>
        </div>
        <div>
          <dt className="text-texto-suave">Retomadas</dt>
          <dd className="text-xl font-bold tabular-nums">{queue.waiting.resume}</dd>
        </div>
        <div>
          <dt className="text-texto-suave">Esteiras</dt>
          <dd className="text-xl font-bold tabular-nums">{queue.waiting.background}</dd>
        </div>
      </dl>
      <p className="mt-3 text-sm text-texto-suave">
        {queue.oldestScreenSince ? (
          <>
            O pedido de tela mais antigo espera desde{' '}
            <Age iso={queue.oldestScreenSince} />.{' '}
          </>
        ) : (
          'Nenhum pedido de tela esperando. '
        )}
        {queue.running === null
          ? 'Nada rodando agora.'
          : queue.running.ours
            ? `Rodando agora: ${queue.running.job ?? 'um job desta base'}.`
            : queue.running.allTenants
              ? `Rodando agora: ${queue.running.job ?? 'um job do relógio'} (todas as bases).`
              : 'O teto está ocupado por outra base agora.'}
      </p>
    </Card>
  );
}

/**
 * Os jobs que esgotaram as tentativas — a fila de mortos que o BullMQ guarda
 * por sete dias. O motivo vem redigido do servidor, sem placa nem documento;
 * para refazer, abre-se a ficha e pede-se de novo. Não há botão de reprocessar
 * aqui de propósito: reprocessar gasta o teto do fornecedor.
 */
function FailuresCard({ failures }: { failures: ErpFailedJob[] }) {
  return (
    <Card
      title="Falhas recentes"
      descricao="Jobs que esgotaram as tentativas nos últimos sete dias."
    >
      {failures.length === 0 ? (
        <p className="text-sm text-texto-suave">
          Nenhum job desta base esgotou as tentativas.
        </p>
      ) : (
        <ul className="flex flex-col gap-3 text-sm">
          {failures.map((f, i) => (
            <li key={`${f.job}-${f.failedAt ?? i}`} className="flex flex-col gap-0.5">
              <span className="font-medium">
                {f.queue === 'projecao' ? 'Projeção' : 'ERP'} · {f.job}
                {f.entity ? ` · ${f.entity}` : ''}
              </span>
              <span className="text-texto-suave">{f.reason}</span>
              <span className="text-xs text-texto-suave">
                {f.attempts} tentativa(s)
                {f.failedAt ? (
                  <>
                    {' '}
                    · <Age iso={f.failedAt} />
                  </>
                ) : null}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}

/* ------------------------------------------------------------------------- *
 * A linha de cada entidade
 * ------------------------------------------------------------------------- */

/**
 * O estado da última passagem — e a distinção que a tela existe para fazer.
 *
 * "Nunca sincronizou" tem selo, cor e frase próprios. Se ele saísse como
 * "concluída" com zero registros, a entidade que nunca rodou ficaria idêntica à
 * que rodou e veio vazia — e das duas, a que precisa de alguém é a primeira.
 */
function EntityState({ sync }: { sync: EntitySync }) {
  if (sync.estado === 'nunca') {
    return <Badge tone="neutro">Nunca sincronizou</Badge>;
  }
  if (sync.estado === 'em_andamento') {
    return <Running progress={sync.progress} />;
  }

  /*
   * "Interrompida" não é "concluída em parte": a passagem parou antes do fim e
   * guardou de onde continuar, e a esteira retoma sozinha. Dizer "em parte"
   * sobre ela pedia de quem lê uma providência que não existe.
   */
  const tone = sync.resuming
    ? 'informacao'
    : sync.result === 'sucesso'
      ? 'sucesso'
      : sync.result === 'parcial'
        ? 'alerta'
        : 'erro';
  const label = sync.resuming
    ? 'Interrompida — continua de onde parou'
    : sync.result === 'sucesso'
      ? 'Concluída'
      : sync.result === 'parcial'
        ? 'Concluída em parte'
        : 'Falhou';

  return (
    <div className="flex flex-col items-start gap-1">
      <Badge tone={tone}>{label}</Badge>
      {/* o erro do servidor, palavra por palavra: é o que se cola num chamado */}
      {sync.detail && <span className="text-xs text-texto-suave">{sync.detail}</span>}
    </div>
  );
}

function LastPass({ sync }: { sync: EntitySync }) {
  if (sync.estado === 'nunca') {
    return <span className="text-texto-suave">Nunca, nesta base</span>;
  }
  if (sync.estado === 'em_andamento') {
    return (
      <span className="text-sm">
        começou <Age iso={sync.startedAt} />
      </span>
    );
  }
  return <Age iso={sync.completedAt} />;
}

/**
 * A passagem aberta, com onde ela está — desde 28/10/2026.
 *
 * A barra só aparece com porcentagem: o servidor manda `percent` nulo quando
 * o total não é conhecido, e aí a linha diz a contagem ("página 12") em vez de
 * desenhar uma barra que a tela teria de inventar.
 *
 * **Sem tique há 10 min é "sem progresso"**, e não "em andamento": uma
 * passagem que anda grava a cada página ou unidade (no máximo a cada 15 s), e a
 * que travou ou cujo processo caiu
 * deixa o último tique para trás. Era o que esta linha não sabia dizer.
 */
function Running({
  progress,
}: {
  progress: Extract<EntitySync, { estado: 'em_andamento' }>['progress'];
}) {
  if (!progress) return <Badge tone="informacao">Em andamento</Badge>;
  const quiet = Date.now() - new Date(progress.at).getTime();
  return (
    <div className="flex w-full min-w-48 flex-col items-start gap-1.5">
      {quiet > PROGRESS_STALE_MS ? (
        <Badge tone="alerta">Sem progresso {formatAge(quiet)}</Badge>
      ) : (
        <Badge tone="informacao">Em andamento</Badge>
      )}
      {progress.percent !== null && (
        <div
          role="progressbar"
          aria-label="Andamento da passagem"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress.percent}
          className="h-1.5 w-full overflow-hidden rounded-pilula bg-superficie-suave"
        >
          <div
            className="h-full rounded-pilula bg-progresso"
            style={{ width: `${progress.percent}%` }}
          />
        </div>
      )}
      <span className="text-xs text-texto-suave tabular-nums">
        {progress.percent !== null && `${progress.percent}% · `}
        {progress.label}
      </span>
      <span className="text-xs text-texto-suave">
        Atualizado <Age iso={progress.at} />
      </span>
    </div>
  );
}

function Records({ sync }: { sync: EntitySync }) {
  if (sync.estado === 'em_andamento' && sync.progress?.records) {
    /* o que já veio do SGA nesta passagem — não é o total do espelho */
    return (
      <span className="tabular-nums">
        {sync.progress.records.toLocaleString('pt-BR')}
        <span className="text-texto-suave"> até agora</span>
      </span>
    );
  }
  if (sync.estado !== 'concluida') {
    return <span className="text-texto-suave">—</span>;
  }
  if (sync.records === null) {
    // nulo é "não sei quantos", e não zero: falha antes da primeira página e
    // passagem que não trouxe nada são fatos opostos
    return <span className="text-texto-suave">não contado</span>;
  }
  return <span className="tabular-nums">{sync.records}</span>;
}

/* ------------------------------------------------------------------------- *
 * A varredura da coleção, por linha
 * ------------------------------------------------------------------------- */

/**
 * Varrer a coleção inteira desta entidade, agora.
 *
 * ## Por que aqui há botão por linha e no formulário abaixo não
 *
 * Porque a pergunta é outra. `POST /erp/sync` atualiza **um registro** e
 * precisa que alguém diga qual — daí o formulário. Esta rota varre a entidade
 * inteira da praça, e a entidade é exatamente o que a linha já é.
 *
 * ## Os três desfechos, e nenhum diz "atualizado"
 *
 * `enfileirada` e `ja_pedida` são os de `sync`, pelos mesmos motivos — quem
 * fala com o ERP é a fila, e "já pedida" é sucesso e não erro.
 *
 * `sem_esteira` é o terceiro, e ele é a razão de o botão **desabilitar** em vez
 * de sumir: a entidade está no espelho e não tem passagem em massa. Um botão
 * ausente faria parecer que a varredura existe e a pessoa não pode pedi-la;
 * desabilitado com o motivo ao lado diz o que é.
 *
 * ## Um aviso de peso, e ele não é enfeite
 *
 * Na CE são 76 mil veículos em 78 chamadas contra um ERP que aceita uma a cada
 * quatro segundos. Quem clica precisa saber que pediu minutos de esteira, e
 * não um refresh.
 */
function SweepButton({ entity }: { entity: EntitySync['entity'] }) {
  const client = useQueryClient();
  const [outcome, setOutcome] = useState<SweepResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  const sweep = useMutation({
    mutationFn: async () => {
      const r = await api.erp.sweep({ body: { entity } });
      return ok(r, 202, 'Não foi possível pedir a varredura.');
    },
    onSuccess: (body) => {
      setError(null);
      setOutcome(body);
      /* O estado da esteira pode ter mudado para "em andamento" — e é o estado
       * que esta tela mostra, não o dado atualizado chegando. */
      void client.invalidateQueries({ queryKey: ['erp', 'saude'] });
    },
    onError: (e: Error) => {
      setOutcome(null);
      setError(e.message);
    },
  });

  const withoutSweep = outcome?.estado === 'sem_esteira';

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        variant="secundario"
        size="sm"
        disabled={withoutSweep}
        loading={sweep.isPending}
        onClick={() => sweep.mutate()}
      >
        Varrer agora
      </Button>

      {outcome?.estado === 'enfileirada' && (
        <p className="text-xs text-texto-suave">
          Na fila. Quem fala com o ERP é a esteira, uma chamada a cada 4s — pode levar
          minutos.
        </p>
      )}
      {outcome?.estado === 'ja_pedida' && (
        <p className="text-xs text-texto-suave">
          Já havia uma varredura de pé, pedida <Age iso={outcome.pedidaAt} />.
        </p>
      )}
      {withoutSweep && (
        <p className="text-xs text-texto-suave">
          Esta entidade não tem varredura em massa — ela é preenchida por outro caminho.
        </p>
      )}
      {error !== null && <p className="text-xs text-perigo">{error}</p>}
    </div>
  );
}

/* ------------------------------------------------------------------------- *
 * O botão "atualizar agora"
 * ------------------------------------------------------------------------- */

/**
 * Atualizar **um registro** agora.
 *
 * ## Por que é um formulário e não um botão por linha da tabela
 *
 * `POST /erp/sync` pede `{ entidade, id }`: a atualização sob demanda é
 * de um registro, não de uma entidade inteira. A tabela acima lista as sete
 * entidades — um botão na linha "Boletos" precisaria inventar um `id`, ou
 * pedir a passagem inteira, que é o job diário e não o botão. Então quem diz
 * qual registro é quem está olhando.
 *
 * As duas tabelas de situação não aparecem na lista de entidades: o ERP as
 * devolve inteiras, não por identificador, e ressincronizá-las é o job de
 * domínio. O contrato já as deixa de fora, e é dele que a lista sai.
 *
 * ## "Atualizado" só com o job terminado
 *
 * Desde 14/10/2026 a rota ESPERA o job, que vai ao ERP fora da fila das
 * esteiras e projeta o que gravou antes de terminar. Os desfechos e as frases
 * são os mesmos das fichas — `RefreshOutcome`, em `common.tsx` —, porque é o
 * mesmo pedido.
 *
 * O botão só aparece para quem tem `erp.sync:executar` — e esconder
 * não protege nada: quem protege é o guard, que nega por omissão. As duas
 * coisas, sempre.
 */
function UpdateRegistry() {
  const [entity, setEntity] = useState<RecordEntity>('associado');
  const [id, setId] = useState('');
  const { mutation: request } = useErpRefresh(['erp', 'saude'], () =>
    id.trim() ? { entity, id: id.trim() } : null,
  );

  return (
    <Card
      title="Atualizar um registro agora"
      descricao="Vai ao ERP na hora, fora da fila das esteiras, e traz o que vem junto do registro."
    >
      {/* Alinhado pelo TOPO, e não pela base: o campo do código carrega a ajuda
          embaixo do input, e com `items-end` era ela que encostava na base da
          fileira — o input subia e ficava fora da linha do select e do botão.
          O botão desce a altura do rótulo (text-xs + gap-1.5 = 1.375rem). */}
      <form
        className="grid items-start gap-3 sm:grid-cols-[11rem_minmax(0,1fr)_auto]"
        onSubmit={(e) => {
          e.preventDefault();
          request.mutate();
        }}
      >
        <div>
          <Select
            label="Entidade"
            value={entity}
            onChange={(e) => setEntity(e.target.value as RecordEntity)}
          >
            {RECORD_ENTITIES.map((e) => (
              <option key={e} value={e}>
                {ENTITY_LABEL[e]}
              </option>
            ))}
          </Select>
        </div>
        <div>
          <Field
            label="Código na origem"
            required
            maxLength={64}
            value={id}
            onChange={(e) => setId(e.target.value)}
            help="O código do registro no ERP — nunca CPF nem placa."
          />
        </div>
        <div className="sm:mt-[1.375rem]">
          <Button type="submit" loading={request.isPending} disabled={!id.trim()}>
            Atualizar agora
          </Button>
        </div>
      </form>

      <div className="mt-3">
        <RefreshOutcome mutation={request} />
      </div>
    </Card>
  );
}
